import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Navigation, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { api, fileURL } from '../../lib/api';
import { setCachedStations, getCachedStations } from '../../lib/offline';
import { syncQueue } from '../../lib/sync';
import { useAuth } from '../../context/AuthContext';

const stationIcon = L.divIcon({
  className: 'custom-marker',
  html: `<div style="width:28px;height:28px;border-radius:50%;background:#1c1c1c;border:3px solid #f8f6f1;box-shadow:0 4px 10px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center"><div style="width:8px;height:8px;background:#f8f6f1;border-radius:50%"></div></div>`,
  iconSize: [28, 28], iconAnchor: [14, 14],
});
const userIcon = L.divIcon({
  className: 'user-marker',
  html: `<div style="width:18px;height:18px;border-radius:50%;background:#2563eb;border:3px solid white;box-shadow:0 0 0 4px rgba(37,99,235,0.25);"></div>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});

// ring: red = marker adoptado · blue = estación propia · dark = competencia
const brandLogoIcon = (logo, ring = '#1c1c1c', size = 30) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:#fff;border:2.5px solid ${ring};box-shadow:0 4px 10px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;overflow:hidden"><img src="${logo}" style="width:${size - 8}px;height:${size - 8}px;object-fit:contain;border-radius:50%"/></div>`,
  iconSize: [size, size], iconAnchor: [size / 2, size / 2],
});

const dotIcon = (ring, size = 28) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:#1c1c1c;border:3px solid ${ring};box-shadow:0 4px 10px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center"><div style="width:8px;height:8px;background:#f8f6f1;border-radius:50%"></div></div>`,
  iconSize: [size, size], iconAnchor: [size / 2, size / 2],
});

const FlyToUser = ({ pos }) => {
  const map = useMap();
  useEffect(() => { if (pos) map.flyTo(pos, 14); }, [pos, map]);
  return null;
};

const MapView = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTM = user?.role === 'territory_manager';
  const scoped = isTM || user?.role === 'agent';
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  const [stations, setStations] = useState([]);
  const [brandLogos, setBrandLogos] = useState({});
  const [loading, setLoading] = useState(true);
  const [userPos, setUserPos] = useState(null);
  const [selected, setSelected] = useState(null);
  const [focusPos, setFocusPos] = useState(null);
  const center = [14.6235, -90.5469];

  useEffect(() => {
    if (!focusId || !stations.length) return;
    const s = stations.find(x => x.id === focusId);
    if (s && s.lat && s.lng) { setSelected(s); setFocusPos([s.lat, s.lng]); }
  }, [focusId, stations]);

  useEffect(() => {
    api.get('/stations')
      .then(r => { setStations(r.data); setCachedStations(r.data); })
      .catch(async () => {
        const cached = await getCachedStations();
        setStations(cached);
      })
      .finally(() => setLoading(false));
    api.get('/brands')
      .then(r => {
        const map = {};
        (r.data || []).forEach(b => { if (b.logo_url) map[b.name.toLowerCase()] = fileURL(b.logo_url); });
        setBrandLogos(map);
      })
      .catch(() => {});
    // Trigger sync of any pending captures on mount
    syncQueue();
  }, []);

  const findMe = () => {
    if (!navigator.geolocation) { setUserPos([14.6235, -90.5469]); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => setUserPos([p.coords.latitude, p.coords.longitude]),
      () => setUserPos([14.6235, -90.5469]),
      { timeout: 5000 }
    );
  };

  // ids de competidores adoptados como marker por alguna estación gestionada visible
  const markerIdSet = useMemo(() => {
    const set = new Set();
    stations.forEach(s => {
      const managed = scoped ? !s.tm_marker_only : s.is_own;
      if (managed) (s.marker_station_ids || []).forEach(id => id && set.add(id));
    });
    return set;
  }, [stations, scoped]);

  // TM/agente: por defecto solo estaciones gestionadas; al tocar una se muestran TODOS sus markers configurados
  const visibleStations = useMemo(() => {
    if (!scoped) return stations;
    const own = stations.filter(s => !s.tm_marker_only);
    if (selected && !selected.tm_marker_only) {
      const ids = new Set(selected.marker_station_ids || []);
      return [...own, ...stations.filter(s => ids.has(s.id))];
    }
    return own;
  }, [scoped, stations, selected]);

  const selectedMarkerCount = useMemo(() => {
    if (!scoped || !selected || selected.tm_marker_only) return 0;
    const ids = new Set(selected.marker_station_ids || []);
    return stations.filter(s => ids.has(s.id)).length;
  }, [scoped, selected, stations]);

  const nearestStations = useMemo(() => {
    const base = scoped ? stations.filter(s => !s.tm_marker_only) : stations;
    if (!userPos) return base.slice(0, 3);
    return [...base]
      .map(s => ({ ...s, d: Math.hypot(s.lat-userPos[0], s.lng-userPos[1]) * 111 }))
      .sort((a, b) => a.d - b.d).slice(0, 3);
  }, [userPos, stations, scoped]);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="animate-spin text-[#8a8579]"/></div>;

  return (
    <div className="relative h-full min-h-[420px]">
      <MapContainer center={center} zoom={12} className="w-full h-full" zoomControl={false}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap'/>
        {visibleStations.map(s => {
          const logo = brandLogos[(s.brand || '').toLowerCase()];
          const isMarker = markerIdSet.has(s.id);
          const ring = isMarker ? '#b91c1c' : (s.is_own ? '#0f1d6f' : '#1c1c1c');
          const size = isMarker ? 34 : (s.is_own ? 32 : 30);
          const icon = logo ? brandLogoIcon(logo, ring, size) : (isMarker || s.is_own ? dotIcon(ring, size) : stationIcon);
          return (
            <Marker key={s.id} position={[s.lat, s.lng]} icon={icon} eventHandlers={{ click: () => setSelected(s) }}>
              <Popup>
                <div className="text-[12px]">
                  <p className="font-medium">{s.name}</p>
                  <p className="text-[#5a5a5a]">{s.address}</p>
                  {(s.is_own || isMarker) && (
                    <p className="mt-1">
                      {s.is_own && <span style={{background:'#e0e7ff',color:'#0f1d6f',borderRadius:8,padding:'1px 6px',fontSize:10,marginRight:4}}>PROPIA</span>}
                      {isMarker && <span style={{background:'#fee2e2',color:'#991b1b',borderRadius:8,padding:'1px 6px',fontSize:10}}>MARKER</span>}
                    </p>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
        {userPos && <Marker position={userPos} icon={userIcon} />}
        {userPos && <FlyToUser pos={userPos} />}
        {focusPos && <FlyToUser pos={focusPos} />}
      </MapContainer>

      <div className="absolute top-3 left-3 right-3 z-[400] bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><MapPin size={14}/><span className="text-[12px] font-medium" data-testid="map-count">{scoped && selected && !selected.tm_marker_only ? `${selectedMarkerCount} marker(s) de ${selected.name}` : `${visibleStations.length} estaciones`}</span></div>
          <Button size="sm" variant="ghost" className="h-8 text-[12px] gap-1.5" onClick={findMe}><Navigation size={13}/> Mi ubicación</Button>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 pt-1.5 border-t border-[#e8e5de]" data-testid="map-legend">
          <span className="inline-flex items-center gap-1.5 text-[10.5px] text-[#5a5a5a]"><span className="w-2.5 h-2.5 rounded-full bg-white border-2 border-[#b91c1c]"/>Marker</span>
          <span className="inline-flex items-center gap-1.5 text-[10.5px] text-[#5a5a5a]"><span className="w-2.5 h-2.5 rounded-full bg-white border-2 border-[#0f1d6f]"/>Propia</span>
          <span className="inline-flex items-center gap-1.5 text-[10.5px] text-[#5a5a5a]"><span className="w-2.5 h-2.5 rounded-full bg-[#1c1c1c]"/>Competencia</span>
        </div>
      </div>

      {selected ? (
        <div className="absolute bottom-4 left-3 right-3 z-[400] bg-white rounded-2xl p-4 shadow-xl slide-up">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-[10px] tracking-wider uppercase text-[#8a8579]">{selected.brand}</p>
              <h3 className="text-[16px] font-medium tracking-tight">{selected.name}</h3>
              <p className="text-[12px] text-[#5a5a5a] mt-0.5">{selected.address}</p>
            </div>
            <button onClick={() => setSelected(null)} className="text-[#8a8579] text-xl leading-none">×</button>
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={() => navigate(`/app/station/${selected.id}`)} className="press-soft flex-1 h-10 bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] rounded-full text-[12px]">Ver detalle</Button>
            {user?.role === 'capturer' && (!selected.is_own || selected.trackable_as_marker) && (
              <Button onClick={() => navigate(`/app/capture/${selected.id}`)} variant="outline" className="press-soft flex-1 h-10 border-[#1c1c1c]/30 text-[#1c1c1c] rounded-full text-[12px]">Capturar precio</Button>
            )}
          </div>
        </div>
      ) : nearestStations.length > 0 && (
        <div className="absolute bottom-4 left-3 right-3 z-[400] bg-white rounded-2xl p-4 shadow-xl">
          <p className="text-[10px] tracking-wider uppercase text-[#8a8579] mb-2">Cercanas</p>
          {nearestStations.map(s => (
            <button key={s.id} onClick={() => setSelected(s)} className="w-full flex items-center justify-between py-2 border-b border-[#e8e5de] last:border-0 text-left">
              <div><p className="text-[13px] font-medium">{s.name}</p><p className="text-[11px] text-[#8a8579]">{s.brand}</p></div>
              <span className="text-[11px] text-[#8a8579]">{userPos ? `${s.d.toFixed(1)} km` : 'Ver'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MapView;
